
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ProfessionalInterface } from '@/services/professionals/types';
import { getProfessionals } from '@/services/professionals';
import { 
  initializeProfessionsCacheNew, 
  getAllProfessions,
  getProfessionById,
  getProfessionLabelNew,
  getDisplayLabel,
  getSpecializationLabel,
  getProfessionForSpecialization,
  isProfession,
  Profession
} from '@/services/professions/getProfessionsNew';
import { israelLocations, israelRegions, loadCitiesFromDB, getCities } from '@/utils/locationData';
import { 
  initializeSettlementsCache, 
  getSettlementAreas, 
  professionalServesCity 
} from '@/utils/settlementUtils';

/**
 * Calculate weighted score that balances rating with review count.
 * Formula: Rating × (1 - 1/(reviewCount + 1))
 * - Professionals with 0 reviews get score 0 (appear last)
 * - More reviews = higher confidence = higher score
 * - High rating with few reviews won't beat lower rating with many reviews
 */
const calculateWeightedScore = (rating: number, reviewCount: number): number => {
  if (reviewCount === 0) return 0;
  const confidenceFactor = 1 - (1 / (reviewCount + 1));
  return rating * confidenceFactor;
};

const useSearchProfessionals = () => {
  const isSearchingRef = useRef(false);
  const [allProfessions, setAllProfessions] = useState<Profession[]>([]);
  const [allProfessionals, setAllProfessionals] = useState<ProfessionalInterface[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [displayProfessionName, setDisplayProfessionName] = useState('');
  const [availableSpecialties, setAvailableSpecialties] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState('rating');
  const [searchResultMessage, setSearchResultMessage] = useState('');
  // NEW: Track the searched profession ID (UUID) for filtering card display
  const [searchedProfessionId, setSearchedProfessionId] = useState<string | null>(null);
  // NEW: Track whether a search has been performed
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState({
    minRating: [4],
    categories: [] as string[],
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const professionParam = queryParams.get('profession');
  const locationParam = queryParams.get('location');
  const displayNameParam = queryParams.get('displayName');

  // Initialize professions, settlements, and cities cache
  useEffect(() => {
    const initCaches = async () => {
      await Promise.all([
        initializeProfessionsCacheNew(),
        initializeSettlementsCache(),
        loadCitiesFromDB()
      ]);
      const profs = getAllProfessions();
      setAllProfessions(profs);
    };
    initCaches();
  }, []);

  // Fetch professionals data
  useEffect(() => {
    const fetchProfessionalsData = async () => {
      try {
        setLoading(true);
        const data = await getProfessionals();
        
        // Ensure we have valid data
        if (!data || data.length === 0) {
          throw new Error('לא נמצאו נותני שירות');
        }
        
        // Make sure each professional has the required fields
        const validatedData = data.map(prof => ({
          ...prof,
          id: prof.id || `prof-${Math.random().toString(36).substr(2, 9)}`,
          name: prof.name || 'נותן שירות',
          profession: prof.profession || prof.category || 'כללי',
          rating: prof.rating || 5,
          reviewCount: prof.reviewCount || 0,
          location: prof.location || prof.area || 'ארצי',
          image: prof.image || 'https://images.unsplash.com/photo-1514539079130-25950c84af65?auto=format&fit=crop&q=80&w=800&h=450',
          specialties: prof.specialties || [],
          verified: prof.verified || false
        }));
        
        setAllProfessionals(validatedData);
        setProfessionals(validatedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching professionals:', err);
        setError('שגיאה בטעינת נותני השירות. אנא נסה שוב מאוחר יותר.');
        setLoading(false);
      }
    };

    fetchProfessionalsData();
  }, []);

  // Handle URL query params - wait for BOTH professionals AND professions cache to load
  useEffect(() => {
    if (!loading && allProfessionals.length > 0 && allProfessions.length > 0) {
      if (professionParam || locationParam) {
        const profession = professionParam || 'all';
        const location = locationParam || 'all';
        
        setSelectedCategory(profession);
        setSelectedLocation(location);
        
        // Pass displayName from URL to preserve Hebrew name
        const providedName = displayNameParam ? decodeURIComponent(displayNameParam) : undefined;
        handleSearch(profession, location, providedName);
      }
    }
  }, [loading, allProfessionals, allProfessions, professionParam, locationParam, displayNameParam]);

  // Update available specialties when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      setAvailableSpecialties([]);
      return;
    }
    
    // Check if it's a profession (UUID)
    const prof = getProfessionById(selectedCategory);
    if (prof) {
      // It's a profession - show its specializations
      const specLabels = prof.specializations.map(sub => sub.label);
      setAvailableSpecialties(specLabels);
      
      // Filter out categories that are no longer valid
      setFilters(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => specLabels.includes(cat))
      }));
    } else {
      // It might be a specialization - find its profession
      const parentProf = getProfessionForSpecialization(selectedCategory);
      if (parentProf) {
        const specLabels = parentProf.specializations.map(sub => sub.label);
        setAvailableSpecialties(specLabels);
        
        setFilters(prev => ({
          ...prev,
          categories: prev.categories.filter(cat => specLabels.includes(cat))
        }));
      } else {
        // Fallback for old professions or unknown IDs
        setAvailableSpecialties([]);
      }
    }
  }, [selectedCategory]);

  // Apply filters when filters or sort option changes
  useEffect(() => {
    if (isSearchingRef.current) {
      return;
    }
    if (allProfessionals.length > 0) {
      applyFilters();
    }
  }, [filters, sortOption]);

  const handleSearch = (profession: string, location: string, providedDisplayName?: string) => {
    isSearchingRef.current = true;
    setHasSearched(true);
    
    setSelectedCategory(profession);
    setSelectedLocation(location);
    
    let filtered = [...allProfessionals];
    let searchMessage = '';
    let searchedProfId: string | null = null;
    
    if (profession !== 'all') {
      // Try to get profession object (works with both UUID and slug)
      const prof = getProfessionById(profession);
      
      if (prof) {
        // It's a main profession - use the UUID for filtering
        const professionUuid = prof.id;
        searchedProfId = professionUuid;
        const profLabel = prof.label;
        
        // Filter by profession_ids array (UUID) or legacy fields
        filtered = filtered.filter(p => {
          // Check profession_ids array using UUID
          if (p.profession_ids && p.profession_ids.length > 0) {
            return p.profession_ids.includes(professionUuid);
          }
          
          // Legacy: Check main_profession or profession text fields
          const matchesMainLabel = p.profession?.trim().toLowerCase() === profLabel?.trim().toLowerCase() || 
                                   p.category?.trim().toLowerCase() === profLabel?.trim().toLowerCase() ||
                                   p.main_profession?.trim().toLowerCase() === profLabel?.trim().toLowerCase();
          
          return matchesMainLabel;
        });
        
        // Fallback: if no results, show all professionals
        if (filtered.length === 0) {
          console.warn('לא נמצאו בעלי מקצוע עבור:', profLabel);
          searchMessage = `לא נמצאו ${profLabel}, מציג את כל בעלי המקצוע`;
          filtered = [...allProfessionals];
          searchedProfId = null;
        }
      } else {
        // It might be a specialization UUID
        const parentProf = getProfessionForSpecialization(profession);
        
        if (parentProf) {
          searchedProfId = parentProf.id; // Use parent profession for card display
          
          // Search for professionals with this specialization
          const exactMatch = filtered.filter(p => {
            // NEW: Check specialization_ids array
            if (p.specialization_ids && p.specialization_ids.length > 0) {
              return p.specialization_ids.includes(profession);
            }
            
            // Legacy: Check sub_specializations
            const hasSubSpec = p.sub_specializations?.includes(profession);
            
            return hasSubSpec;
          });
          
          if (exactMatch.length > 0) {
            filtered = exactMatch;
            const specLabel = getSpecializationLabel(profession);
            searchMessage = `מציג ${parentProf.label} המתמחים ב-${specLabel}`;
          } else {
            // No exact match - show all professionals with parent profession
            filtered = filtered.filter(p => {
              if (p.profession_ids && p.profession_ids.length > 0) {
                return p.profession_ids.includes(parentProf.id);
              }
              
              const matchesMainLabel = p.profession === parentProf.label || 
                                       p.main_profession === parentProf.label ||
                                       p.category === parentProf.label;
              
              return matchesMainLabel;
            });
            
            const specLabel = getSpecializationLabel(profession);
            searchMessage = `לא נמצאו ${parentProf.label} המתמחים ב-${specLabel}, מציג את כל ה-${parentProf.label}`;
          }
        } else {
          // Fallback for legacy profession text IDs
          filtered = filtered.filter(p => 
            p.profession === profession || 
            p.category === profession || 
            p.specialty === profession ||
            p.main_profession === profession
          );
        }
      }
    }
    
    if (location !== 'all') {
      const cities = getCities();
      const locationLabel =
        cities.find(l => l.id === location)?.label ||
        israelLocations.find(l => l.id === location)?.label ||
        israelRegions.find(r => r.id === location)?.label ||
        location;

      // Get settlement areas for the selected city
      const settlementAreas = getSettlementAreas(locationLabel);
      
      filtered = filtered.filter(p => 
        professionalServesCity(
          { areas: p.areas, work_cities: p.work_cities },
          locationLabel,
          settlementAreas
        )
      );
    }
    
    setSearchResultMessage(searchMessage);
    setSearchedProfessionId(searchedProfId);
    // Use provided display name from URL if available, otherwise use getDisplayLabel for full label
    if (providedDisplayName) {
      setDisplayProfessionName(providedDisplayName);
    } else {
      setDisplayProfessionName(profession === 'all' ? 'כל המקצועות' : getDisplayLabel(profession));
    }
    
    // Sort and display results using weighted score algorithm
    let sorted = [...filtered];
    sorted.sort((a, b) => {
      const scoreA = calculateWeightedScore(a.rating || 0, a.reviewCount || 0);
      const scoreB = calculateWeightedScore(b.rating || 0, b.reviewCount || 0);
      return scoreB - scoreA;
    });
    setProfessionals(sorted);
    
    setTimeout(() => {
      isSearchingRef.current = false;
    }, 100);
  };
  
  const applyFilters = (baseList = [...allProfessionals]) => {
    if (isSearchingRef.current) {
      return;
    }
    
    let filtered = [...baseList];
    
    // Only apply category/location filters if baseList is the full list (not pre-filtered)
    if (baseList === allProfessionals || baseList.length === allProfessionals.length) {
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(p => 
          p.profession === selectedCategory || 
          p.category === selectedCategory || 
          p.specialty === selectedCategory
        );
      }
      
      if (selectedLocation !== 'all') {
        const cities = getCities();
        const locationLabel =
          cities.find(l => l.id === selectedLocation)?.label ||
          israelLocations.find(l => l.id === selectedLocation)?.label ||
          israelRegions.find(r => r.id === selectedLocation)?.label ||
          selectedLocation;
        
        const settlementAreas = getSettlementAreas(locationLabel);
        
        filtered = filtered.filter(p => 
          professionalServesCity(
            { areas: p.areas, work_cities: p.work_cities },
            locationLabel,
            settlementAreas
          )
        );
      }
    }
    
    // Apply additional filters
    if (filters.minRating[0] > 0) {
      filtered = filtered.filter(p => p.rating >= filters.minRating[0]);
    }
    
    if (filters.categories.length > 0) {
      filtered = filtered.filter(p => {
      if (p.specialization_ids && p.specialization_ids.length > 0) {
        // NEW: Check if any specialization ID matches
        const profSpecLabels = p.specialization_ids
          .map(specId => getSpecializationLabel(specId))
          .filter(label => label !== null) as string[];
        
        return filters.categories.some(cat => profSpecLabels.includes(cat));
      }
      
      // Legacy: Check sub_specializations
      if (p.sub_specializations && p.sub_specializations.length > 0) {
        const professionalSubLabels = p.sub_specializations
          .map(subId => getSpecializationLabel(subId) || subId)
          .filter(label => label !== null) as string[];
        
        return filters.categories.some(cat => professionalSubLabels.includes(cat));
      }
      
      // Fallback: Check specialties array
      if (p.specialties && p.specialties.length > 0) {
        return filters.categories.some(cat => p.specialties.includes(cat));
      }
      
      // No specializations - don't show
      return false;
    });
    }
    
    sortProfessionals(filtered);
  };
  
  const sortProfessionals = (list = professionals) => {
    let sorted = [...list];
    
    switch (sortOption) {
      case 'rating':
        // Smart weighted sort - balances rating with review count
        sorted.sort((a, b) => {
          const scoreA = calculateWeightedScore(a.rating || 0, a.reviewCount || 0);
          const scoreB = calculateWeightedScore(b.rating || 0, b.reviewCount || 0);
          return scoreB - scoreA;
        });
        break;
      case 'popularity':
        sorted.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        break;
      case 'name_asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // Default also uses weighted score
        sorted.sort((a, b) => {
          const scoreA = calculateWeightedScore(a.rating || 0, a.reviewCount || 0);
          const scoreB = calculateWeightedScore(b.rating || 0, b.reviewCount || 0);
          return scoreB - scoreA;
        });
    }
    
    setProfessionals(sorted);
  };
  
  const toggleCategory = (category: string) => {
    setFilters(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      
      return { ...prev, categories };
    });
  };
  
  const handleFilterApply = () => {
    applyFilters();
    setMobileFiltersOpen(false);
  };
  
  const resetFilters = () => {
    setFilters({
      minRating: [4],
      categories: [],
    });
    
    applyFilters();
  };

  const resetSearch = () => {
    setHasSearched(false);
    setSelectedCategory('all');
    setSelectedLocation('all');
    setDisplayProfessionName('');
    setSearchResultMessage('');
    setSearchedProfessionId(null);
    setProfessionals(allProfessionals);
  };
  
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value);
    sortProfessionals();
  };
  
  const handlePhoneReveal = (professionalName: string) => {
    console.log(`Phone revealed for: ${professionalName}`);
    return true;
  };

  return {
    professionals,
    loading,
    error,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    selectedCategory,
    selectedLocation,
    displayProfessionName,
    availableSpecialties,
    sortOption,
    filters,
    setFilters,
    handleSearch,
    toggleCategory,
    handleFilterApply,
    resetFilters,
    handleSortChange,
    handlePhoneReveal,
    searchResultMessage,
    // NEW: The searched profession ID for card display filtering
    searchedProfessionId,
    // NEW: Whether a search has been performed
    hasSearched,
    // NEW: Reset search to go back to landing view
    resetSearch
  };
};

export default useSearchProfessionals;
